<template>
    <v-card class="mb-2">
        <v-row>
            <v-col sm="4">
                <h3 class="mt-5 pl-3">
                    <v-btn icon flat link @click="remove">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    {{ uploadFile.datei.name }}
                </h3>
            </v-col>
            <v-col sm="1">
                <h3 class="mt-5">{{ uploadFile.getReadableFileSizeString() }}</h3>
            </v-col>
            <v-col sm="3">
                <v-select v-model="uploadFile.dokumentTyp" class="mt-5" :items="typen" dense hide-details></v-select>
            </v-col>
            <v-col sm="3">
                <!-- <v-text-field
                    v-model="uploadFile.schlagworte"
                    :placeholder="$t('upload.table.schlagworte')"
                ></v-text-field> -->
                <v-combobox
                    ref="combobox"
                    v-model="uploadFile.schlagwortVorschlaege"
                    v-model:search="schlagwortSearch"
                    :items="schlagwortSelection"
                    :label="$t('upload.table.schlagworte')"
                    :hide-no-data="!schlagwortSearch"
                    class="mt-3"
                    append-icon=""
                    multiple
                    bg-color="white"
                    :custom-filter="customFilter"
                    :clear-on-select="true"
                    :hide-selected="true"
                    @keydown.space.prevent="simulateEnter"
                >
                    <template #selection="{ attrs, item, selected, index }">
                        <v-chip v-bind="attrs" :input-value="selected" label small closable @click:close="deleteSchlagwortFromSelection(index, item.raw)">
                            <span class="pr-2">
                                {{ item.value }}
                            </span>
                        </v-chip>
                    </template>
                    <template #item="{ props, item, index }">
                        <v-list-item v-if="item.raw.header && search">
                            <span class="mr-3">Create</span>
                            <v-chip size="small" variant="flat" label>
                                {{ search }}
                            </v-chip>
                        </v-list-item>
                        <v-list-subheader v-else-if="item.raw.header" :title="item.title"></v-list-subheader>
                        <v-list-item v-else @click="props.onClick">
                            <v-chip :text="item.raw" variant="outlined"></v-chip>
                            <template #append>
                                <v-btn
                                    icon="mdi-delete"
                                    size="small"
                                    variant="text"
                                    @click.stop.prevent="deleteSchlagwortFromDatabase(index, item.raw)"
                                ></v-btn>
                            </template>
                        </v-list-item>
                    </template>
                </v-combobox>
            </v-col>
        </v-row>
    </v-card>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';
import { DokumentUploadDto } from '@/types/DokumentUploadDto';

import { NAME as NAME_UPLOAD } from '../store';
const uploadModul = namespace(NAME_UPLOAD);
import { GET_SCHLAGWORTE, CREATE_SCHLAGWORT, DELETE_SCHLAGWORT } from '../store/actions';
import { SCHLAGWORTVORSCHLAEGE } from '../store/getters';

@Component
export default class UploadRow extends Vue {
    @Prop({ required: false, type: Object })
    private uploadFile!: DokumentUploadDto;

    @Prop({ required: false, type: Array })
    private typen!: Array<object>;

    @uploadModul.Action(GET_SCHLAGWORTE) getSchlagworte!: Function;
    @uploadModul.Action(CREATE_SCHLAGWORT) createSchlagwort!: Function;
    @uploadModul.Action(DELETE_SCHLAGWORT) deleteSchlagwort!: Function;
    @uploadModul.Getter(SCHLAGWORTVORSCHLAEGE)
    schlagwortSelection!: Array<string>;

    schlagwortSearch = '';
    schlagwortEdit = '';

    remove() {
        this.$emit('remove', this.uploadFile.uuid);
    }

    addNewSchlagwortFromRow(item) {
        const itemText = item.trim();
        if (itemText === '') {
            this.schlagwortSearch = '';
            return;
        }
        this.uploadFile.schlagwortVorschlaege.push(itemText);
        this.uploadFile.schlagworte = this.uploadFile.schlagwortVorschlaege.join(' ');
        if (!this.schlagwortSelection.includes(itemText)) {
            this.createSchlagwort(item);
        }
        this.schlagwortSearch = '';
    }

    deleteSchlagwortFromSelection(index, item) {
        if (index > -1) {
            this.schlagwortSelection.splice(index, 1);
            this.uploadFile.schlagwortVorschlaege.splice(index, 1);
            this.uploadFile.schlagworte = this.uploadFile.schlagwortVorschlaege.join(' ');
        }
    }
    deleteSchlagwortFromDatabase(index, item) {
        this.deleteSchlagwort(item);
        this.getSchlagworte(this.schlagwortSearch);
    }

    @Watch('uploadFile.schlagwortVorschlaege')
    onSelectedSchlagworteChanged(newVal: any, oldVal: any) {
        this.uploadFile.schlagworte = this.uploadFile.schlagwortVorschlaege.join(' ');
        if (newVal.length === oldVal.length || newVal.length < oldVal.length) {
            return;
        }

        // Get the new value that was added
        const newEntry = newVal[newVal.length - 1];
        // Suchstrings mit wildcards werden nur für suche verwendet, aber nie als schlagwort erstellt
        if (typeof newEntry === 'string' && newEntry.includes('*')) {
            this.uploadFile.schlagwortVorschlaege.pop();
            return;
        }

        if (this.schlagwortSelection.includes(newVal[newVal.length - 1])) {
            return;
        }

        //Dieser Teil wird nur Ausgeführt wenn ein neues Schlagwort hinzugefügt wird.
        if (newVal[newVal.length - 1] != undefined) {
            this.createSchlagwort(newVal[newVal.length - 1]);
        }
    }

    @Watch('schlagwortSearch')
    onSearchStringChanged() {
        this.bouncedSearchStringChange();
    }

    private bouncedSearchStringChange = debounce(function (this: UploadRow) {
        if (this.schlagwortSearch != null) {
            if (this.schlagwortSearch.trim() != '') {
                this.getSchlagworte(this.schlagwortSearch);
            }
        }
    }, 400);

    simulateEnter(event: Event) {
        // Hack, damit bei 'Space' der gleiche Event ausgelöst wird wie bei 'Enter'.
        // Grund: Der Suchstring kann nicht manuell zurückgesetzt werden
        event.preventDefault();
        const enterEvent = new KeyboardEvent('keydown', {
            key: 'Enter',
            code: 'Enter',
            keyCode: 13,
            bubbles: true
        });

        event.target.dispatchEvent(enterEvent);
    }
    customFilter(item: string, queryText: string): boolean {
        // Ersetze "*" mit dem Regex-Platzhalter-Muster
        const searchText = queryText.replace(/\*/g, '.*');
        // Erstelle ein Regex-Muster und entferne Sonderzeichen ausser "*"
        const regex = new RegExp(searchText.replace(/[-/\\^$+?.()|[\]{}]/g, '\\$&'), 'i');
        return regex.test(item);
    }
}
</script>
